/**
 * PatternSpotlightCard - Layer 2 Premium Component
 *
 * Editorial grid card for highlighting communication patterns
 * Visible to Drama+ (Layer 2) and DramaX (Layer 3) subscribers
 *
 * Features:
 * - 12-column editorial grid layout
 * - Gold accent styling (#C8A882)
 * - Neue Haas Grotesk Display typography (Layer 2)
 * - 280ms tier-reveal-animation
 * - Share functionality with analytics tracking
 *
 * Grid Structure:
 * - Header: Pattern name (8 cols) + Share CTA (4 cols)
 * - Description: Full width (12 cols)
 * - Footer: Frequency badge (6 cols)
 */

import React from 'react';
import { View, Pressable } from 'react-native';
import { useTranslation } from '@dramadetector/shared';
import { TierCard } from './TierCard';
import { TierHeadline, TierBody } from './TierTypography';

export interface PatternSpotlightCardProps {
  /** Pattern type name (e.g., "Defensive Language", "Hedging") */
  patternType: string;

  /** Pattern explanation or description */
  description: string;

  /** Number of times pattern was detected */
  frequency: number;

  /** Optional context or additional details */
  context?: string;

  /** Current subscription tier level (1-3). May be undefined during loading. */
  tierLevel: number | undefined;

  /** Share button handler with analytics tracking */
  onShare?: () => void;

  /** Additional CSS classes */
  className?: string;

  /** Test ID for automated testing */
  testID?: string;
}

export function PatternSpotlightCard({
  patternType,
  description,
  frequency,
  context,
  tierLevel,
  onShare,
  className = '',
  testID = 'pattern-spotlight-card',
}: PatternSpotlightCardProps) {
  const { t } = useTranslation(['features', 'common']);

  // Tier visibility check - Layer 2+ only
  // Guard against undefined/NaN: `undefined < 2` is false in JS, which would bypass this check.
  if (!tierLevel || tierLevel < 2) {
    return null;
  }

  return (
    <TierCard
      variant="premium"
      className={`tier-reveal-animation ${className}`}
      testID={testID}
    >
      {/* 12-column grid container */}
      <View className="grid grid-cols-12 gap-4">
        {/* Header Row: Pattern Name (8 cols) + Share CTA (4 cols) */}
        <View className="col-span-8">
          <TierHeadline
            size="lg"
            weight="bold"
            color="text-gray-900"
            testID={`${testID}-pattern-name`}
          >
            {patternType}
          </TierHeadline>
        </View>

        {onShare && (
          <View className="col-span-4 flex items-end justify-start">
            <Pressable
              onPress={onShare}
              className="px-4 py-2 border border-accent-gold rounded-tier bg-white active:bg-accent-blush transition-colors"
              testID={`${testID}-share-button`}
              accessibilityLabel={t('features:patternSpotlightCard.accessibility.sharePattern', { patternType, defaultValue: 'Share {{patternType}} pattern' })}
            >
              <TierBody
                size="base"
                weight="medium"
                color="text-accent-gold"
              >
                {t('common:share')}
              </TierBody>
            </Pressable>
          </View>
        )}

        {/* Description Row: Full Width (12 cols) */}
        <View className="col-span-12">
          <TierBody
            size="base"
            color="text-gray-700"
            className="leading-relaxed"
            testID={`${testID}-description`}
          >
            {description}
          </TierBody>
        </View>

        {/* Optional Context Row */}
        {context && (
          <View className="col-span-12">
            <TierBody
              size="sm"
              color="text-gray-500"
              className="italic"
              testID={`${testID}-context`}
            >
              {context}
            </TierBody>
          </View>
        )}

        {/* Footer Row: Frequency Badge (6 cols) */}
        <View className="col-span-6">
          <View className="inline-flex items-center px-3 py-1 bg-accent-blush border-l-2 border-accent-gold rounded-tier">
            <TierBody
              size="sm"
              weight="semibold"
              color="text-gray-900"
              testID={`${testID}-frequency`}
            >
              {t('features:patternSpotlightCard.detected', { count: frequency, defaultValue: 'Detected {{count}} times' })}
            </TierBody>
          </View>
        </View>
      </View>
    </TierCard>
  );
}
